import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isGuestActor } from "../helpers/guestMode";
import {
  CONNECTIVITY_CHANGED_EVENT,
  CONNECTIVITY_STATE,
  isOffline,
} from "../helpers/connectivity";
import {
  resolveOrganizationForActor,
  resolveOrganizationIdFromActor,
  updateOrganization,
  saveOrganizationWorkSchedule,
} from "../helpers/organizationHelpers";
import {
  DEFAULT_ORG_SETTINGS,
  getEffectiveOrgSetting,
  mergeAppSettings,
  organizationRowToAppSettings,
} from "../helpers/organizationSettings";
import {
  hasLocalBridgeMigrationCompleted,
  loadCachedOrgSettings,
  loadCachedOrganizationId,
  markLocalBridgeMigrationCompleted,
  persistCachedOrgSettings,
} from "../helpers/orgSettingsBridge";

export const OrganizationContext = createContext(null);

function shouldMigrateBridgeToDatabase() {
  if (hasLocalBridgeMigrationCompleted()) {
    return false;
  }

  try {
    return localStorage.getItem("CP_ORG_SETTINGS_V1") !== null;
  } catch {
    return false;
  }
}

export function OrganizationProvider({ actor, children }) {
  const [organization, setOrganization] = useState(null);
  const [settings, setSettings] = useState(() => loadCachedOrgSettings());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const organizationRef = useRef(null);

  const applyOrganizationState = useCallback((row) => {
    organizationRef.current = row;
    setOrganization(row);
    const nextSettings = organizationRowToAppSettings(row);
    setSettings(nextSettings);
    persistCachedOrgSettings(nextSettings, row?.id);
  }, []);

  const refreshOrganization = useCallback(async () => {
    if (!actor || isGuestActor(actor)) {
      setOrganization(null);
      setSettings({ ...DEFAULT_ORG_SETTINGS });
      return;
    }

    if (isOffline()) {
      const orgId = resolveOrganizationIdFromActor(actor);
      setSettings(loadCachedOrgSettings(orgId));
      const cachedId =
        loadCachedOrganizationId() || orgId;
      if (cachedId) {
        setOrganization((current) => current || { id: cachedId });
      }
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const row = await resolveOrganizationForActor(actor);
      if (!row) {
        setSettings(loadCachedOrgSettings(resolveOrganizationIdFromActor(actor)));
        return;
      }

      if (shouldMigrateBridgeToDatabase()) {
        const bridgeSettings = loadCachedOrgSettings();
        const migrated = await updateOrganization(row.id, bridgeSettings, row);
        applyOrganizationState(migrated.row);
        markLocalBridgeMigrationCompleted();
        return;
      }

      applyOrganizationState(row);
    } catch (err) {
      console.warn("[OrganizationProvider] load failed — using cache", err);
      setError(err);
      setSettings(loadCachedOrgSettings(resolveOrganizationIdFromActor(actor)));
    } finally {
      setLoading(false);
    }
  }, [actor, applyOrganizationState]);

  useEffect(() => {
    refreshOrganization();
  }, [refreshOrganization]);

  useEffect(() => {
    const handleConnectivityRestored = (event) => {
      if (event.detail?.state === CONNECTIVITY_STATE.GOOD) {
        refreshOrganization();
      }
    };

    window.addEventListener(CONNECTIVITY_CHANGED_EVENT, handleConnectivityRestored);
    return () => {
      window.removeEventListener(
        CONNECTIVITY_CHANGED_EVENT,
        handleConnectivityRestored,
      );
    };
  }, [refreshOrganization]);

  const updateSettings = useCallback(
    async (patch) => {
      const merged = mergeAppSettings(settings, patch);
      setSettings(merged);
      persistCachedOrgSettings(merged, organizationRef.current?.id);

      if (!organizationRef.current?.id || isGuestActor(actor)) {
        return merged;
      }

      if (isOffline()) {
        return merged;
      }

      try {
        const result = await updateOrganization(
          organizationRef.current.id,
          patch,
          organizationRef.current,
        );
        applyOrganizationState(result.row);
        return result.settings;
      } catch (err) {
        console.warn("[OrganizationProvider] save failed — cached locally", err);
        setError(err);
        return merged;
      }
    },
    [actor, applyOrganizationState, settings],
  );

  // Work schedule saves are online-only and server-confirmed: unlike
  // updateSettings there is no local-only fallback, so a refused or offline
  // save is reported to the caller instead of looking saved.
  const saveWorkSchedule = useCallback(
    async (doc) => {
      if (!organizationRef.current?.id || isGuestActor(actor)) {
        throw new Error("No organization is loaded.");
      }
      if (isOffline()) {
        throw new Error("You are offline. Connect to save the work schedule.");
      }
      const result = await saveOrganizationWorkSchedule(organizationRef.current.id, doc);
      applyOrganizationState(result.row);
      return result.settings;
    },
    [actor, applyOrganizationState],
  );

  const value = useMemo(
    () => ({
      organization,
      settings,
      loading,
      error,
      updateSettings,
      saveWorkSchedule,
      refreshOrganization,
      getSetting: (path, fallback) =>
        getEffectiveOrgSetting(settings, path, fallback),
    }),
    [organization, settings, loading, error, updateSettings, saveWorkSchedule, refreshOrganization],
  );

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error("useOrganization must be used within OrganizationProvider");
  }
  return context;
}
